import { useMemo } from 'react';
import { MapPin, TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import { getRankBgClass, getRankChange } from '@/utils/rankUtils';
import { formatDate, truncate } from '@/utils/formatUtils';
import TrendSparkline from './TrendSparkline';

export default function KeywordCard({ ranking, history, index }) {
  const { keyword, location, rank, snippet, url, scanned_at, competitors } = ranking;

  const previousRank = useMemo(() => {
    if (!history || history.length < 2) return null;
    return history[history.length - 2]?.rank;
  }, [history]);

  const change = getRankChange(rank, previousRank);

  return (
    <div 
      className="card fade-in"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`keyword-card-${keyword.replace(/\s/g, '-')}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold font-syne text-[#E2E8F0] break-words">{keyword}</h3>
          <div className="flex items-center gap-1 mt-1">
            <MapPin size={10} className="text-[#4A5568]" />
            <span className="text-[10px] font-mono text-[#4A5568]">{location}</span>
          </div>
        </div>
        
        {/* Rank Badge */}
        <div className={`rank-badge ${getRankBgClass(rank)}`}>
          {rank !== null && rank !== undefined ? rank : '—'}
        </div>
      </div>

      {/* Sparkline */}
      {history && history.length > 1 && (
        <div className="h-12 mb-3">
          <TrendSparkline data={history} />
        </div>
      )}

      {/* Change Indicator */}
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center gap-1 text-xs font-mono ${
          change.direction === 'up' ? 'text-[#00C48C]' : 
          change.direction === 'down' ? 'text-[#EF4444]' : 
          'text-[#4A5568]'
        }`}>
          {change.direction === 'up' && <TrendingUp size={12} />}
          {change.direction === 'down' && <TrendingDown size={12} />}
          {change.direction === 'flat' && <Minus size={12} />}
          <span>{change.label}</span>
        </div>
        <span className="text-[10px] font-mono text-[#4A5568]">
          {formatDate(scanned_at)}
        </span>
      </div>

      {/* Snippet */}
      {snippet && (
        <p className="text-xs text-[#94A3B8] italic mb-3 line-clamp-2">
          "{truncate(snippet, 80)}"
        </p>
      )}

      {/* Competitors */}
      {competitors && competitors.length > 0 && (
        <div className="border-t border-[#1E2D45] pt-3">
          <div className="text-[10px] font-mono text-[#4A5568] uppercase mb-2">Top Competitors</div>
          <div className="flex flex-wrap gap-1">
            {competitors.slice(0, 3).map((comp, i) => (
              <span 
                key={i} 
                className="text-[10px] font-mono text-[#94A3B8] bg-[#1A2338] px-2 py-0.5 rounded"
              >
                {truncate(comp, 20)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* URL Link */}
      {url && (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-[10px] font-mono text-[#0070F3] hover:text-[#60A5FA] mt-3 transition-colors"
        >
          <ExternalLink size={10} />
          View Result
        </a>
      )}
    </div>
  );
}
